import { createElement, type AriaAttributes, type MouseEventHandler, type ReactNode } from 'react'

import { cn } from '@/lib/cn'

export type SelectableCardElement = 'button' | 'div' | 'label'

export interface SelectableCardProps extends AriaAttributes {
  as?: SelectableCardElement
  children: ReactNode
  className?: string
  disabled?: boolean
  id?: string
  onClick?: MouseEventHandler<HTMLElement>
  role?: string
  selected?: boolean
  selectedClassName?: string
  tabIndex?: number
  type?: 'button' | 'reset' | 'submit'
  unselectedClassName?: string
}

export function SelectableCard({
  as = 'div',
  children,
  className,
  disabled,
  onClick,
  selected = false,
  selectedClassName,
  type,
  unselectedClassName,
  ...props
}: SelectableCardProps) {
  return createElement(
    as,
    {
      ...props,
      'aria-disabled': as === 'button' ? undefined : (disabled ?? props['aria-disabled']),
      className: cn(
        'rounded-lg border bg-white transition-colors',
        selected
          ? (selectedClassName ?? 'border-[var(--color-primary)] bg-[rgba(30,95,255,0.06)]')
          : (unselectedClassName ??
              'border-[var(--color-control-border)] hover:border-[var(--color-control-border-hover)] hover:bg-[var(--color-surface-muted)]'),
        disabled ? 'cursor-not-allowed opacity-70' : null,
        className,
      ),
      disabled: as === 'button' ? disabled : undefined,
      onClick,
      type: as === 'button' ? (type ?? 'button') : undefined,
    },
    children,
  )
}

export function SelectableCardIndicator({ selected }: { selected: boolean }) {
  return (
    <span
      className={cn(
        'mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border',
        selected
          ? 'border-[var(--color-primary)] bg-[var(--color-primary)] text-white'
          : 'border-[var(--color-control-border)] bg-white',
      )}
    >
      {selected ? (
        <svg aria-hidden className="size-3" fill="none" viewBox="0 0 12 12">
          <path
            d="m2.5 6.2 2.1 2.1 4.9-5"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.6"
          />
        </svg>
      ) : null}
    </span>
  )
}
